/* eslint-disable import/no-cycle */
import React, { useContext, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { createUseStyles } from '@glu/theming';
import { withFormContext } from '@glu/validation-react';
import classnames from 'classnames';
import get from 'lodash/get';
import isEmpty from 'lodash/isEmpty';
import Spinner from 'components/spinner/Spinner';
import styles from './PaymentDetails.styles';
import AppPropTypes from '../../../proptypes/AppPropTypes';
import { getAvailableBusinessDays } from '../../../api/payments/service';

// components
import BankCodeArea from '../BankCodeArea';
import TransactionDate from '../components/TransactionDate';
import Amount from '../components/Amount';
import PurposeOfWire from '../components/PurposeOfWire';
import Charges from '../components/Charges';
import AmountType from '../components/AmountType';
import ExchangeRate from '../components/ExchangeRate';
import CustomRateConfirmation from '../components/CustomRateConfirmation';
import Input from '../components/WireInput';
import LocaleContext from '../../locale/LocaleContext';

import { maxCharactersTemplate } from '../../gluValidation/utils';
import { lt, regexp } from '../../../utils/validations/gluValidations';
import { MATCHERS, PAYMENT_TYPES } from '../../../constants';
import { specialChars } from '../utils';

const useStyles = createUseStyles(styles);

const FormContextWrapper = ({ children, data }) => children({ formData: data });
const FormContext = withFormContext(FormContextWrapper);

const PaymentDetailsComponent = ({
  action,
  data,
  formChange,
  formState,
  fromTemplate,
  isTemplate,
  onLoad,
  onParentUpdate,
  preloaded,
  services,
  source,
  type,
  validate
}) => {
  const [businessDays, setBusinessDays] = useState();
  const [loading, setLoading] = useState(true);
  const templateRelated = action.match(/(.+?)-template/g);
  const locale = useContext(LocaleContext);
  const classes = useStyles();
  const { wirePreferences } = data;
  const domestic =
    get(formState, 'paymentType[0].type', formState.paymentType) === PAYMENT_TYPES.WIRE.DOMESTIC;

  useEffect(() => {
    if (isTemplate || !isEmpty(businessDays)) {
      setLoading(false);
      return () => {};
    }

    const { currency, paymentType } = formState;

    let apiReqCurrency = currency;
    if (Array.isArray(currency)) apiReqCurrency = currency[0].currency;
    if (typeof currency === 'object') apiReqCurrency = currency.currency;

    const subscription = getAvailableBusinessDays({
      currency: apiReqCurrency,
      paymentMethod: Array.isArray(paymentType)
        ? paymentType[0]?.type.toLowerCase()
        : paymentType.toLowerCase()
    }).subscribe((data) => {
      setBusinessDays(data);
      setLoading(false);
    });
    return () => subscription.unsubscribe();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isTemplate, formState]);

  if (loading) {
    return <Spinner />;
  }

  return (
    <FormContext>
      {({ formData }) => {
        const usd = formData.isUSD;
        return (
          <div className={classnames(classes.root, 'col-xs-12')}>
            <h3 className={classes.title}>{locale['paymentDetails.header']}</h3>

            <div className={classes.fields}>
              <div className={classes.row}>
                <BankCodeArea
                  field="beneficiaryBankInfo"
                  type={type}
                  formState={formState}
                  formChange={formChange}
                  fromTemplate={fromTemplate}
                  preloaded={preloaded}
                  isTemplate={isTemplate}
                  locale={locale}
                  onLoad={onLoad}
                  data={data}
                  required
                  asRow
                >
                  {() => (
                    <Input
                      id="accountNumber"
                      name="accountNumber"
                      label={locale['common.accountNumber']}
                      className="col-xs-3"
                      debounceInput
                      defaultValue={get(formState.beneficiaryAccountInfo, 'accountNumber')}
                      maxLength={34}
                      labelClass={classes.labelClass}
                      validators={[
                        regexp({
                          regexp: MATCHERS.ALLOWED_CHAR_REGEX,
                          msg: locale['validation.regexp']
                        }),
                        specialChars({
                          locale,
                          paymentType: formState.paymentType,
                          prefs: data.wirePreferences
                        }),
                        { required: true, msg: locale['validation.required'] },
                        lt({ maxLength: 34, msg: maxCharactersTemplate(locale, 34) })
                      ]}
                      disabled={fromTemplate}
                    />
                  )}
                </BankCodeArea>
              </div>

              <div className={classes.row}>
                <AmountType
                  {...{
                    formState: source,
                    action,
                    data,
                    onParentUpdate,
                    isTemplate,
                    formChange
                  }}
                />
                <div className="col-xs-3">
                  <Amount
                    {...{
                      formState,
                      action,
                      data,
                      onParentUpdate,
                      isTemplate,
                      businessDays
                    }}
                  />
                </div>
              </div>
              {!templateRelated && !usd && !domestic && (
                <div className={classes.row}>
                  <ExchangeRate
                    {...{
                      formState: source,
                      data,
                      onParentUpdate,
                      formChange,
                      isTemplate
                    }}
                  />
                  <CustomRateConfirmation
                    {...{
                      formState: source,
                      formChange,
                      source,
                      data,
                      isTemplate
                    }}
                  />
                </div>
              )}
              {!templateRelated && (
                <div className={classes.row}>
                  <div className="col-xs-3">
                    <TransactionDate
                      validate={validate}
                      services={services}
                      formState={formState}
                      formChange={formChange}
                      data={data}
                      fromTemplate={fromTemplate}
                      isTemplate={isTemplate}
                    />
                  </div>
                  {wirePreferences.mtsChargesDisplay && (
                    <Charges formState={formState} data={data} isTemplate={isTemplate} />
                  )}
                </div>
              )}

              {!templateRelated && (
                <div className={classes.row}>
                  <div className="col-xs-3">
                    <PurposeOfWire formState={formState} data={data} isTemplate={isTemplate} />
                  </div>
                </div>
              )}
            </div>
          </div>
        );
      }}
    </FormContext>
  );
};

PaymentDetailsComponent.propTypes = {
  classes: AppPropTypes.jssClasses.isRequired,
  locale: AppPropTypes.locale.isRequired,
  type: PropTypes.string.isRequired,
  formState: PropTypes.shape({}).isRequired,
  formChange: PropTypes.func.isRequired,
  fromTemplate: PropTypes.bool.isRequired,
  onLoad: PropTypes.func.isRequired,
  services: PropTypes.shape({}).isRequired,
  validate: PropTypes.func.isRequired,
  onChange: PropTypes.func.isRequired,
  data: PropTypes.shape({}).isRequired,
  action: PropTypes.string.isRequired,
  onParentUpdate: PropTypes.func.isRequired,
  preloaded: PropTypes.bool.isRequired,
  source: PropTypes.shape({}).isRequired,
  isTemplate: PropTypes.bool.isRequired
};

export default PaymentDetailsComponent;
